/**
 * HeroBlock — Iter 174
 *
 * Layout-aware hero renderer that mirrors the preview shapes in
 * IntroShapes.js. Dispatches off `block.layout`:
 *   • centered-hero / hero-fullbleed → full-bleed image + centered overlay
 *   • floating-card                  → bg image + floating offset white card
 *   • overlapping                    → bg image + geometric overlapping block
 *   • collage                        → 3-image collage backdrop
 *   • large-typography / minimal     → no image, big serif type
 *   • brand-showcase                 → featured pill + side image
 *   • video-hero                     → image + play-button overlay
 *   • multi-image                    → 3-column image grid + caption
 *   • layered                        → diagonal skewed text overlay
 *   • offset-asymmetric              → asymmetric text/image grid
 *   • hero-left-classic              → split, image left, text right
 *
 * Falls back to the legacy centered hero when layout is missing or
 * unrecognized, so any pre-Iter-174 'hero' block keeps rendering.
 */
import React from 'react';
import { getWbButtonAttrs } from '../sqs/WbButton';
import WBImage from '../sqs/imageFrameSystem/WBImage';

const Btn = ({ btn, i, variant = 'dark' }) => {
  const attrs = getWbButtonAttrs(i);
  return (
    <span
      {...attrs}
      className={`px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer ${attrs.className} ${
        variant === 'dark' ? 'bg-white text-gray-900' : 'bg-[#0E0E23] text-white'
      }`}
    >{btn.text}</span>
  );
};

/**
 * Iter 183 — CRITICAL FIX: accept `E` as a prop from the parent
 * (DashboardContent.js exports the stable, module-level `StableInlineE`
 * as `E`). Previously this file created a per-render `useInline()`
 * factory that returned a brand-new arrow-function component on every
 * call (`const E = useInline(block, onEdit, fieldStyles)`). Because
 * the returned function's identity changed every render, React saw a
 * different component type each time, unmounted the previous
 * <InlineText> tree, and mounted a fresh one. That unmount/mount cycle
 * is what caused the user-visible "fading text" flicker in the hero
 * section while authoring (especially when the parent re-rendered due
 * to mouse-move state churn). Every other block — AboutBlock,
 * GalleryBlock, ProductsBlock, etc. — was already migrated to accept
 * `E` as a prop in iter 160; only HeroBlock was left behind.
 * Defaulted to a no-op span so the file is still independently
 * usable in tests / storybook.
 */
export default function HeroBlock({ block, content, onEdit, fieldStyles = {}, onMediaPick, E }) {
  const c = content || block.content || {};
  const frame = (c._image_frames || {}).image_url;
  const layout = block.layout || 'centered-hero';
  const img = c.image_url;
  const bgImg = img ? (
    <WBImage
      src={img} alt="" blockId={block.id} field="image_url" frame={frame}
      className="absolute inset-0 w-full h-full"
      imgStyle={{ objectPosition: c.focus_point ? `${c.focus_point.x ?? 50}% ${c.focus_point.y ?? 50}%` : '50% 50%' }}
    />
  ) : (
    <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black" />
  );

  // LARGE TYPOGRAPHY / MINIMAL — no image, type-driven
  if (layout === 'large-typography' || layout === 'minimal') {
    const isMinimal = layout === 'minimal';
    return (
      <div data-hero-layout={layout} className={`relative ${isMinimal ? 'bg-[#FAF6EE]' : 'bg-white'} text-gray-900 px-6 py-24 sm:py-32 text-center`}>
        <E value={c.title} field="title" className={`${isMinimal ? 'text-6xl sm:text-7xl' : 'text-6xl sm:text-8xl'} font-bold leading-none font-syne tracking-tight`} tag="h1" placeholder="BOLD. quiet. FOREVER." />
        <E value={c.tagline} field="tagline" className="text-xs uppercase tracking-[0.3em] text-gray-500 mt-6" tag="p" placeholder="Subtitle" />
      </div>
    );
  }

  // FLOATING CARD HERO — image + floating offset card
  if (layout === 'floating-card') {
    return (
      <div data-hero-layout="floating-card" className="relative min-h-[480px] overflow-hidden">
        {bgImg}
        <div className="absolute inset-0 bg-black/15 pointer-events-none" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 flex items-end min-h-[480px]">
          <div className="bg-white shadow-2xl p-8 sm:p-10 max-w-md ml-auto">
            <E value={c.eyebrow || 'STUDIO'} field="eyebrow" className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#8B5CF6]" tag="p" placeholder="STUDIO" />
            <E value={c.title} field="title" className="text-3xl font-bold text-[#0E0E23] mt-3 leading-tight font-syne" tag="h1" placeholder="Design that breathes." />
            <E value={c.tagline} field="tagline" className="text-sm text-gray-600 mt-2" tag="p" placeholder="Subtitle" />
            <div className="mt-6 flex gap-2">{(c.buttons || []).map((b, i) => <Btn key={i} btn={b} i={i} variant="light" />)}</div>
          </div>
        </div>
      </div>
    );
  }

  // OVERLAPPING — image + overlapping rotated geometric block
  if (layout === 'overlapping') {
    return (
      <div data-hero-layout="overlapping" className="relative min-h-[480px] overflow-hidden">
        {bgImg}
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20">
          <div className="bg-[#0E0E23] text-white inline-block p-8 sm:p-10 -rotate-1 shadow-2xl">
            <E value={c.title} field="title" className="text-4xl font-bold leading-none font-syne" tag="h1" placeholder="Tomorrow begins today." />
            <E value={c.tagline} field="tagline" className="text-[10px] uppercase tracking-[0.3em] mt-3 opacity-80" tag="p" placeholder="Atelier · 2026" />
            <div className="mt-5">{(c.buttons || []).map((b, i) => <Btn key={i} btn={b} i={i} />)}</div>
          </div>
        </div>
      </div>
    );
  }

  // COLLAGE — 3-image grid backdrop + centered text
  if (layout === 'collage') {
    const imgs = c.images || [];
    return (
      <div data-hero-layout="collage" className="relative min-h-[480px] bg-[#FAF6EE] grid grid-cols-3 gap-2 p-2">
        {[0,1,2].map((i) => (
          <div key={i} className="aspect-square overflow-hidden">
            <WBImage src={imgs[i] || `https://picsum.photos/seed/${block.id}-collage-${i}/600/600`} alt="" blockId={block.id} field={`images.${i}`} className="w-full h-full object-cover" />
          </div>
        ))}
        <div className="col-span-3 px-6 py-12 text-center">
          <E value={c.title} field="title" className="text-4xl sm:text-5xl font-bold text-[#0E0E23] font-syne" tag="h1" placeholder="Made with intention." />
          <E value={c.tagline} field="tagline" className="text-sm text-gray-600 mt-3 max-w-xl mx-auto" tag="p" placeholder="A collection of moments, objects, and craft." />
        </div>
      </div>
    );
  }

  // BRAND SHOWCASE — featured pill + side image
  if (layout === 'brand-showcase') {
    return (
      <div data-hero-layout="brand-showcase" className="relative bg-white grid grid-cols-1 md:grid-cols-2 min-h-[440px]">
        <div className="p-10 sm:p-16 flex flex-col justify-center">
          <span className="inline-flex items-center self-start gap-1.5 px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-[10px] uppercase tracking-wider font-bold">★ Featured</span>
          <E value={c.title} field="title" className="text-4xl sm:text-5xl font-bold text-[#0E0E23] mt-5 leading-tight font-syne" tag="h1" placeholder="Featured: Maya" />
          <E value={c.tagline} field="tagline" className="text-base text-gray-600 mt-3" tag="p" placeholder="Spring Collection · 24 pieces" />
          <div className="mt-6 flex gap-3">{(c.buttons || []).map((b, i) => <Btn key={i} btn={b} i={i} variant="light" />)}</div>
        </div>
        <div className="relative min-h-[260px]">{bgImg}</div>
      </div>
    );
  }

  // VIDEO HERO — image + play button overlay
  if (layout === 'video-hero') {
    return (
      <div data-hero-layout="video-hero" className="relative min-h-[480px] overflow-hidden">
        {bgImg}
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex flex-col items-center justify-center min-h-[480px] text-white text-center px-6">
          <button className="w-20 h-20 rounded-full bg-white/15 backdrop-blur ring-2 ring-white/40 flex items-center justify-center mb-6 hover:bg-white/25 transition">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
          </button>
          <E value={c.title} field="title" className="text-4xl sm:text-5xl font-bold font-syne" tag="h1" placeholder="See it in motion" />
          <E value={c.tagline} field="tagline" className="text-xs uppercase tracking-[0.3em] mt-4 opacity-80" tag="p" placeholder="2 min watch · 4K" />
        </div>
      </div>
    );
  }

  // MULTI-IMAGE — 3-column image grid + caption
  if (layout === 'multi-image') {
    const imgs = c.images || [];
    return (
      <div data-hero-layout="multi-image" className="relative bg-white py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <E value={c.title} field="title" className="text-4xl sm:text-5xl font-bold text-[#0E0E23] text-center font-syne" tag="h1" placeholder="Three collections." />
          <E value={c.tagline} field="tagline" className="text-sm text-gray-500 text-center mt-3 mb-10" tag="p" placeholder="Spring · Summer · Fall" />
          <div className="grid grid-cols-3 gap-3">
            {['Spring','Summer','Fall'].map((label, i) => (
              <div key={i} className="relative aspect-[3/4] overflow-hidden">
                <WBImage src={imgs[i] || `https://picsum.photos/seed/${block.id}-multi-${i}/600/800`} alt="" blockId={block.id} field={`images.${i}`} className="w-full h-full object-cover" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3"><p className="text-white text-[10px] uppercase tracking-[0.25em] font-bold">{label}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // LAYERED — diagonal skewed text overlay
  if (layout === 'layered') {
    return (
      <div data-hero-layout="layered" className="relative min-h-[480px] overflow-hidden">
        {bgImg}
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-transparent to-transparent" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 min-h-[480px] flex items-center">
          <div className="text-white max-w-lg" style={{ transform: 'skewY(-1deg)' }}>
            <E value={c.title} field="title" className="text-5xl sm:text-6xl font-bold leading-[1.05] font-syne" tag="h1" placeholder="For the bold, the slow, the careful." />
            <E value={c.tagline} field="tagline" className="text-xs uppercase tracking-[0.3em] mt-5 opacity-85" tag="p" placeholder="Capsule 02" />
          </div>
        </div>
      </div>
    );
  }

  // OFFSET ASYMMETRIC — text on left in narrow column, image on right offset
  if (layout === 'offset-asymmetric') {
    return (
      <div data-hero-layout="offset-asymmetric" className="relative bg-[#FAF6EE] grid grid-cols-1 md:grid-cols-[1fr_1.3fr] gap-8 px-6 py-16 items-center">
        <div>
          <E value={c.eyebrow || 'Issue 12'} field="eyebrow" className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#8B5CF6]" tag="p" placeholder="Issue 12" />
          <E value={c.title} field="title" className="text-5xl sm:text-6xl font-bold text-[#0E0E23] mt-3 leading-[1.05] font-syne" tag="h1" placeholder="Less to see." />
          <div className="mt-6">{(c.buttons || []).map((b, i) => <Btn key={i} btn={b} i={i} variant="light" />)}</div>
        </div>
        <div className="relative aspect-[4/3] -mb-12 md:-mb-0 md:translate-y-8">{bgImg}</div>
      </div>
    );
  }

  // HERO LEFT CLASSIC — image left, text right (legacy introduce-hero-left)
  if (layout === 'hero-left-classic') {
    return (
      <div data-hero-layout="hero-left-classic" className="relative bg-white grid grid-cols-1 md:grid-cols-2 min-h-[440px]">
        <div className="relative min-h-[260px]">{bgImg}</div>
        <div className="p-10 sm:p-16 flex flex-col justify-center">
          <E value={c.title} field="title" className="text-4xl sm:text-5xl font-bold text-[#0E0E23] leading-tight font-syne" tag="h1" placeholder="Welcome to our story" />
          <E value={c.tagline} field="tagline" className="text-base text-gray-600 mt-3" tag="p" placeholder="Crafting moments that matter." />
          <div className="mt-6 flex gap-3">{(c.buttons || []).map((b, i) => <Btn key={i} btn={b} i={i} variant="light" />)}</div>
        </div>
      </div>
    );
  }

  // ─── Iter 189c Phase B — NEW premium hero variants ───────────────
  // SPLIT-SCREEN HALF-COVER — asymmetric 40/60 split. Text card on left
  // (dark bg, oversize headline). Full-bleed image on right taking 60%
  // width. Ideal for shops that want a strong product/lifestyle photo.
  if (layout === 'split-screen-halfcover') {
    return (
      <div data-hero-layout="split-screen-halfcover" className="relative grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] min-h-[540px]">
        <div className="relative bg-[#0E0E23] text-white p-12 sm:p-16 flex flex-col justify-center">
          {c.eyebrow && <E value={c.eyebrow} field="eyebrow" className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#A78BFA]" tag="p" placeholder="Collection · 2026" />}
          <E value={c.title} field="title" className="text-5xl sm:text-6xl font-bold leading-[1.02] mt-4 font-syne tracking-tight" tag="h1" placeholder="Objects with weight. Meaning with reach." />
          <E value={c.tagline} field="tagline" className="text-base text-white/70 mt-5 leading-relaxed max-w-sm" tag="p" placeholder="Every piece hand-finished. Every story yours to tell." />
          <div className="mt-8 flex gap-3">{(c.buttons || []).map((b, i) => <Btn key={i} btn={b} i={i} variant="dark" />)}</div>
        </div>
        <div className="relative min-h-[360px] md:min-h-full overflow-hidden">
          {bgImg}
          <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-[#0E0E23]/40 to-transparent pointer-events-none hidden md:block" />
        </div>
      </div>
    );
  }

  // ── Iter 189c Phase B · Portfolio — NEW `case-study-hero` ──
  // Magazine featured case-study hero: client eyebrow → oversize serif
  // project title → outcome tagline → 3 metric badges (e.g. 3× conversion,
  // $2M revenue, 6-week build) → hero image → CTA "Read the case study".
  if (layout === 'case-study-hero') {
    const metrics = c.metrics || [{ label: 'CONVERSION', value: '3×' }, { label: 'REVENUE UPLIFT', value: '$2M' }, { label: 'BUILD TIME', value: '6 wk' }];
    return (
      <div data-hero-layout="case-study-hero" className="relative bg-[#FAF6EE] px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
            <div>
              <E value={c.eyebrow} field="eyebrow" className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#8B5CF6]" tag="p" placeholder="CASE STUDY · KINDRED" />
              <E value={c.title} field="title" className='mt-5 text-5xl md:text-6xl font-bold leading-[1.02] text-[#0E0E23] font-["Playfair_Display","Georgia",serif]' tag="h2" placeholder="How we tripled Kindred's conversion." />
              <E value={c.tagline} field="tagline" className="mt-5 text-lg text-[#0E0E23]/70 leading-relaxed max-w-md" tag="p" placeholder="A full-service brand overhaul, delivered in 6 weeks. Here's what we did — and what it moved." />
              <div className="mt-8 grid grid-cols-3 gap-6 border-y border-[#0E0E23]/10 py-6">
                {metrics.slice(0, 3).map((m, i) => (
                  <div key={i}>
                    <div className="text-3xl md:text-4xl font-bold text-[#0E0E23] font-syne">{m.value}</div>
                    <div className="mt-1 text-[9px] uppercase tracking-[0.25em] font-bold text-[#0E0E23]/60">{m.label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex gap-3">{(c.buttons || [{ text: 'Read the case study', link: '#' }]).map((b, i) => <Btn key={i} btn={b} i={i} variant="light" />)}</div>
            </div>
            <div className="relative aspect-[4/5] overflow-hidden">
              {bgImg || (c.image_url && <img src={c.image_url} alt="" className="w-full h-full object-cover" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // TESTIMONIAL HERO — quote-first hero. Big serif quote as headline,
  // author photo/name/role below, optional CTA. Trust-building for
  // service businesses + high-consideration purchases.
  if (layout === 'testimonial-hero') {
    const author = c.author || {};
    const stars = Math.max(0, Math.min(5, Number(c.stars ?? 5)));
    return (
      <div data-hero-layout="testimonial-hero" className="relative bg-[#FAF6EE] min-h-[520px] px-6 py-24 flex items-center">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center gap-1 mb-6">
            {Array.from({ length: stars }).map((_, i) => (
              <svg key={i} width={20} height={20} viewBox="0 0 12 12"><path d="M6 0l1.7 4 4.3.3-3.3 2.7 1 4.2L6 9l-3.7 2.2 1-4.2L0 4.3l4.3-.3z" fill="#F59E0B"/></svg>
            ))}
          </div>
          <E value={c.title || c.quote} field={c.title ? 'title' : 'quote'} className='text-3xl sm:text-4xl md:text-5xl font-bold text-[#0E0E23] leading-[1.15] font-["Playfair_Display","Georgia",serif] italic' tag="blockquote" placeholder="&ldquo;They rebuilt our brand from the ground up — and doubled our revenue in six months.&rdquo;" />
          <div className="mt-10 flex items-center justify-center gap-4">
            {(author.image_url || c.image_url) && (
              <img src={author.image_url || c.image_url} alt="" className="w-14 h-14 rounded-full object-cover" />
            )}
            <div className="text-left">
              <E value={author.name || c.author_name} field={author.name ? 'author.name' : 'author_name'} className="text-sm font-bold text-[#0E0E23]" tag="p" placeholder="Amelia Ross" />
              <E value={author.role || c.author_role} field={author.role ? 'author.role' : 'author_role'} className="text-xs text-[#0E0E23]/60 mt-0.5" tag="p" placeholder="Founder, Kindred Studios" />
            </div>
          </div>
          {(c.buttons || []).length > 0 && (
            <div className="mt-10 flex gap-3 justify-center">{(c.buttons || []).map((b, i) => <Btn key={i} btn={b} i={i} variant="light" />)}</div>
          )}
        </div>
      </div>
    );
  }

  // DEFAULT (centered-hero / hero-fullbleed / unknown) — original behavior
  return (
    <div data-hero-layout={layout || 'centered-hero'} className="relative min-h-[420px] flex items-center justify-center overflow-hidden">
      {bgImg}
      <div className="absolute inset-0 bg-black/40 pointer-events-none" />
      <div className="relative z-10 text-center text-white px-8 max-w-3xl">
        {c.eyebrow && <E value={c.eyebrow} field="eyebrow" className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-90 mb-3" tag="p" placeholder="Eyebrow" />}
        <E value={c.title} field="title" className="text-4xl md:text-5xl font-bold leading-tight drop-shadow-lg" tag="h1" placeholder="Your Headline" />
        <E value={c.tagline} field="tagline" className="text-lg md:text-xl opacity-90 mt-4 drop-shadow" tag="p" placeholder="Your tagline goes here" />
        <div className="flex gap-3 justify-center mt-8">{(c.buttons || []).map((btn, i) => <Btn key={i} btn={btn} i={i} variant="dark" />)}</div>
      </div>
    </div>
  );
}
